import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
  notification,
} from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  CloudUploadOutlined,
  WarningTwoTone,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { apiClient } from "../../../services/api/apiClient";
import { certificationSyncService } from "../../../services/api/certificationSyncService";
import type { CertificationSyncReport } from "../../../types/certification-sync";

const { Text } = Typography;

interface AuditLike {
  id: number;
  reference?: string | null;
  status_of_audit?: string | null;
  audit_state?: { name?: string | null } | null;
  aforo_source_id?: string | null;
  aforo_synced_at?: string | null;
  aforo_last_error?: string | null;
  farm?: {
    id?: number | null;
    name?: string | null;
    producer?: {
      id?: number | null;
      name?: string | null;
    } | null;
  } | null;
}

interface Props {
  audit: AuditLike;
  onRefresh: () => void;
}

/**
 * Card admin-only en el detalle de auditoría: dispara el push manual
 * del aforo agregado a Certification (`POST /api/audits/{id}/push-aforo/`)
 * y muestra el estado del último envío (timestamp o motivo del fallo).
 */
/** Normaliza para comparar nombres tolerando espacios y capitalización. */
const norm = (s: string | null | undefined) =>
  (s || "").trim().toLowerCase().replace(/\s+/g, " ");

export const AforoPushCard = ({ audit, onRefresh }: Props) => {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);

  const hasSyncedOK = !!audit.aforo_synced_at;
  const hasError = !!(audit.aforo_last_error || "").trim();

  // --- Vista previa del remoto en Certification -----------------
  // Reutiliza el endpoint /admin/integrations/certification/producers/{id}/status/
  // que ya consume la pestaña de sincronización del productor —
  // así confirmamos in-place que el productor+finca destino
  // existen y que los nombres coinciden antes de pulsar Enviar.
  const producerId = audit.farm?.producer?.id ?? null;
  const farmId = audit.farm?.id ?? null;
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<CertificationSyncReport | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!producerId) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    certificationSyncService
      .getProducerStatus(producerId)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setPreviewError(
            e?.response?.data?.detail ||
              e?.message ||
              t(
                "audit.aforo.preview_error",
                "No se pudo consultar el estado remoto.",
              ),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [producerId, t]);

  const remoteProducerName = preview?.remote?.producer?.name ?? null;
  const remoteFarm =
    farmId && preview?.remote?.farms
      ? preview.remote.farms.find((f) => f.database_id === farmId) ?? null
      : null;
  const producerNameMismatch =
    preview?.remote_status === "found" &&
    !!remoteProducerName &&
    !!audit.farm?.producer?.name &&
    norm(remoteProducerName) !== norm(audit.farm.producer.name);
  const farmNameMismatch =
    !!remoteFarm &&
    !!remoteFarm.name &&
    !!audit.farm?.name &&
    norm(remoteFarm.name) !== norm(audit.farm.name);
  // Doble vía para "finalizada" — misma lógica que `isFinalized` en
  // `audits/show.tsx` (línea 574). Hay auditorías legacy con
  // `status_of_audit='planned'` pero `audit_state.name='Finalizada'`
  // que están operativamente cerradas y sí se deben poder enviar.
  const canPush =
    (audit.status_of_audit || "").toLowerCase() === "finalized" ||
    /finaliz/i.test(audit.audit_state?.name ?? "");

  const runPush = async () => {
    setSending(true);
    try {
      const resp = await apiClient.post(
        `/audits/${audit.id}/push-aforo/`,
      );
      const data = resp?.data || {};
      const kg = Number(data.estimated_kg_auditor ?? 0);
      const body = data.aforo_response_body || {};
      // El body incluye `created` (true en 201, false en 200
      // idempotente) y `changed` (true si el 200 aplicó cambios).
      const created = !!body.created;
      const changed = !!body.changed;
      const summary = created
        ? t(
            "audit.aforo.push_created",
            "Aforo creado en Certification ({{kg}} kg).",
            { kg: kg.toLocaleString("es-ES") },
          )
        : changed
          ? t(
              "audit.aforo.push_updated",
              "Aforo actualizado en Certification ({{kg}} kg).",
              { kg: kg.toLocaleString("es-ES") },
            )
          : t(
              "audit.aforo.push_noop",
              "Aforo ya estaba sincronizado ({{kg}} kg) — sin cambios.",
              { kg: kg.toLocaleString("es-ES") },
            );
      message.success(summary);
      notification.success({
        message: t(
          "audit.aforo.push_success_title",
          "Aforo enviado a Certification",
        ),
        description: (
          <>
            <div>{summary}</div>
            {body.farm_database_id && (
              <div style={{ marginTop: 4, fontSize: 12 }}>
                <Text type="secondary">
                  Farm #{body.farm_database_id} · Season #{body.season_id ?? "?"}
                </Text>
              </div>
            )}
          </>
        ),
        placement: "topRight",
        duration: 6,
      });
      onRefresh();
    } catch (err: any) {
      const resp =
        err?.originalError?.response?.data ||
        err?.response?.data ||
        err?.data ||
        {};
      const detail =
        resp?.detail ||
        (typeof resp?.errors === "object"
          ? Object.entries(resp.errors)
              .map(([k, v]) => `${k}: ${String(v)}`)
              .join(" · ")
          : null) ||
        err?.message ||
        t(
          "audit.aforo.push_error_generic",
          "No se pudo enviar el aforo a Certification.",
        );
      message.error(detail);
      notification.error({
        message: t(
          "audit.aforo.push_error_title",
          "Error al enviar el aforo",
        ),
        description: detail,
        placement: "topRight",
        duration: 10,
      });
      onRefresh();
    } finally {
      setSending(false);
    }
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <Space>
          <CloudUploadOutlined />
          <strong>
            {t("audit.aforo.card_title", "Aforo → Certification")}
          </strong>
        </Space>
      }
      extra={
        <Popconfirm
          title={t(
            "audit.aforo.confirm_title",
            "Enviar aforo a Certification",
          )}
          description={
            <div style={{ maxWidth: 340 }}>
              {t(
                "audit.aforo.confirm_body",
                "Se enviará la suma de kilos estimados de las parcelas auditadas a Certification. Si ya existe un envío previo con el mismo identificador y los datos coinciden, se marcará como no-op.",
              )}
            </div>
          }
          okText={t("audit.aforo.confirm_ok", "Enviar")}
          cancelText={t("common.cancel", "Cancelar")}
          onConfirm={runPush}
        >
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={sending}
          >
            {hasSyncedOK
              ? t("audit.aforo.resend_btn", "Reenviar aforo")
              : t("audit.aforo.send_btn", "Enviar aforo")}
          </Button>
        </Popconfirm>
      }
    >
      <Descriptions
        column={{ xs: 1, sm: 2 }}
        size="small"
        colon={false}
        labelStyle={{ color: "#8c8c8c", fontSize: 12 }}
      >
        <Descriptions.Item
          label={t("audit.aforo.last_sync", "Último envío OK")}
        >
          {hasSyncedOK ? (
            <Space>
              <Tag color="green">
                {dayjs(audit.aforo_synced_at!).format("DD/MM/YYYY HH:mm")}
              </Tag>
              {audit.aforo_source_id && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {audit.aforo_source_id}
                </Text>
              )}
            </Space>
          ) : (
            <Text type="secondary">
              {t("audit.aforo.never_sent", "Nunca enviado")}
            </Text>
          )}
        </Descriptions.Item>
        {audit.aforo_source_id && !hasSyncedOK && (
          <Descriptions.Item
            label={t("audit.aforo.source_id_label", "Source ID")}
          >
            <Text code>{audit.aforo_source_id}</Text>
          </Descriptions.Item>
        )}
      </Descriptions>
      {producerId && (
        <div
          style={{
            marginTop: 8,
            padding: "6px 10px",
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          {previewLoading && (
            <Space size={6}>
              <Spin size="small" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(
                  "audit.aforo.preview_loading",
                  "Consultando destino en Certification…",
                )}
              </Text>
            </Space>
          )}
          {!previewLoading && previewError && (
            <Space size={6}>
              <WarningTwoTone twoToneColor="#faad14" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {previewError}
              </Text>
            </Space>
          )}
          {!previewLoading && !previewError && preview && (
            <Space direction="vertical" size={2} style={{ width: "100%" }}>
              {/* Estado global (disabled / auth_error / etc.) — sólo se
                  muestra cuando no es "found"/"not_found", que ya se
                  cubren específicamente abajo. */}
              {preview.remote_status !== "found" &&
                preview.remote_status !== "not_found" && (
                  <Space size={6}>
                    <WarningTwoTone twoToneColor="#faad14" />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t(
                        `certification_sync.remote_status_${preview.remote_status}`,
                        `Estado remoto: ${preview.remote_status}`,
                      )}
                    </Text>
                  </Space>
                )}

              {/* Productor remoto */}
              <Space size={6} wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("audit.aforo.preview_producer", "Productor remoto")}:
                </Text>
                {preview.remote_status === "found" && remoteProducerName ? (
                  <>
                    <CheckCircleTwoTone twoToneColor="#52c41a" />
                    <Text style={{ fontSize: 12 }}>{remoteProducerName}</Text>
                    {producerNameMismatch && (
                      <Tooltip
                        title={t(
                          "audit.aforo.preview_producer_mismatch_tooltip",
                          "El nombre remoto difiere del nombre local del productor ({{local}}).",
                          { local: audit.farm?.producer?.name ?? "" },
                        )}
                      >
                        <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>
                          {t(
                            "audit.aforo.preview_name_mismatch",
                            "Nombre difiere del local",
                          )}
                        </Tag>
                      </Tooltip>
                    )}
                  </>
                ) : preview.remote_status === "not_found" ? (
                  <>
                    <CloseCircleTwoTone twoToneColor="#f5222d" />
                    <Text type="danger" style={{ fontSize: 12 }}>
                      {t(
                        "audit.aforo.preview_producer_not_found",
                        "No existe en Certification",
                      )}
                    </Text>
                  </>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    —
                  </Text>
                )}
              </Space>

              {/* Finca remota — sólo tiene sentido si el productor existe. */}
              {preview.remote_status === "found" && (
                <Space size={6} wrap>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("audit.aforo.preview_farm", "Finca remota")}:
                  </Text>
                  {remoteFarm ? (
                    <>
                      <CheckCircleTwoTone twoToneColor="#52c41a" />
                      <Text style={{ fontSize: 12 }}>
                        {remoteFarm.name || `#${remoteFarm.database_id}`}
                      </Text>
                      {farmNameMismatch && (
                        <Tooltip
                          title={t(
                            "audit.aforo.preview_farm_mismatch_tooltip",
                            "El nombre de la finca en Certification difiere del local ({{local}}).",
                            { local: audit.farm?.name ?? "" },
                          )}
                        >
                          <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>
                            {t(
                              "audit.aforo.preview_name_mismatch",
                              "Nombre difiere del local",
                            )}
                          </Tag>
                        </Tooltip>
                      )}
                    </>
                  ) : (
                    <>
                      <CloseCircleTwoTone twoToneColor="#f5222d" />
                      <Text type="danger" style={{ fontSize: 12 }}>
                        {t(
                          "audit.aforo.preview_farm_not_found",
                          "No existe — el push la creará",
                        )}
                      </Text>
                    </>
                  )}
                </Space>
              )}
            </Space>
          )}
        </div>
      )}
      {!canPush && (
        <Alert
          type="warning"
          banner
          showIcon
          style={{ marginTop: 8, padding: "4px 12px", fontSize: 12 }}
          message={
            <span style={{ fontSize: 12 }}>
              {t(
                "audit.aforo.not_finalized_inline",
                "Auditoría no finalizada — finalízala primero para poder enviarla.",
              )}
            </span>
          }
        />
      )}
      {hasError && (
        <Alert
          type="error"
          banner
          showIcon
          style={{ marginTop: 8, padding: "4px 12px", fontSize: 12 }}
          message={
            <Tooltip
              placement="topLeft"
              title={
                <span style={{ whiteSpace: "pre-wrap" }}>
                  {audit.aforo_last_error}
                </span>
              }
            >
              <span style={{ fontSize: 12 }}>
                {t("audit.aforo.last_error_short", "Último error: ")}
                <Text
                  ellipsis
                  style={{
                    fontSize: 12,
                    color: "#a8071a",
                    maxWidth: 420,
                    display: "inline-block",
                    verticalAlign: "bottom",
                  }}
                >
                  {audit.aforo_last_error}
                </Text>
              </span>
            </Tooltip>
          }
        />
      )}
    </Card>
  );
};

export default AforoPushCard;
